(() => {
  const t = (key, values = {}, fallback = key) => window.NLV?.i18n?.t(key, values, fallback) ?? String(fallback).replace(/\{([a-zA-Z_][\w]*)\}/g, (match, name) => Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : match);
  const initialized = new WeakSet();
  let instagramReady;

  function loadInstagramSdk() {
    if (typeof window.instgrm?.Embeds?.process === 'function') return Promise.resolve(window.instgrm);
    if (instagramReady) return instagramReady;
    instagramReady = new Promise((resolve, reject) => {
      let script = document.querySelector('script[src="https://www.instagram.com/embed.js"],script[src="//www.instagram.com/embed.js"]');
      const created = !script;
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://www.instagram.com/embed.js';
        script.async = true;
        script.setAttribute('data-nlv-instagram-sdk', '');
      }
      let finished = false;
      const finish = error => {
        if (finished) return;
        finished = true;
        clearTimeout(timeout);
        script.removeEventListener('load', loaded);
        script.removeEventListener('error', failed);
        if (error) {
          if (created) script.remove();
          instagramReady = undefined;
          reject(error);
        } else resolve(window.instgrm);
      };
      const loaded = () => finish(typeof window.instgrm?.Embeds?.process === 'function' ? null : new Error(t('videos.unavailable', {}, "Instagram is unavailable.")));
      const failed = () => finish(new Error(t('videos.unavailable', {}, "Instagram is unavailable.")));
      const timeout = setTimeout(failed, 12000);
      script.addEventListener('load', loaded, { once: true });
      script.addEventListener('error', failed, { once: true });
      if (created) document.head.append(script);
    });
    return instagramReady;
  }

  function initialize(section) {
    if (initialized.has(section)) return;
    initialized.add(section);
    const controller = new AbortController();
    const on = (target, type, callback, options = {}) => target?.addEventListener(type, callback, { ...options, signal: controller.signal });
    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const track = section.querySelector('[data-video-track]');
    const cards = [...section.querySelectorAll('[data-video-card]')];
    const previous = section.querySelector('[data-video-previous]');
    const next = section.querySelector('[data-video-next]');
    const status = section.querySelector('[data-video-status]');
    const dialog = section.querySelector('[data-video-dialog]');
    const content = section.querySelector('[data-video-dialog-content]');
    const motionButton = section.querySelector('[data-video-motion]');
    const automatic = section.hasAttribute('data-video-autoscroll') && !!track;
    const speed = Math.min(40, Math.max(10, Number(section.dataset.videoSpeed) || 22));
    let trigger;
    let previousOverflow = '';
    let scrollFrame;
    let motionFrame;
    let embedRequest = 0;
    let playerObserver;
    let playerFrame;
    let playerLoad;
    let embedTimeout;
    let motionTimestamp = 0;
    let motionPosition = 0;
    let direction = 1;
    let userPaused = false;
    let hovering = false;
    let focused = false;
    let touching = false;
    let editorSelected = false;
    let onScreen = !('IntersectionObserver' in window);
    let resumeTimer;
    let resumeAfter = 0;
    let destroyed = false;

    const maximumScroll = () => track ? Math.max(0, track.scrollWidth - track.clientWidth) : 0;
    const behavior = () => motionPreference.matches ? 'instant' : 'smooth';

    function position(index) {
      if (!track || !cards[index]) return 0;
      return cards[index].getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
    }

    function currentIndex() {
      if (!track) return 0;
      let nearest = 0;
      let distance = Infinity;
      cards.forEach((card, index) => {
        const difference = Math.abs(position(index) - track.scrollLeft);
        if (difference < distance) { nearest = index; distance = difference; }
      });
      return nearest;
    }

    function updateControls() {
      if (previous) previous.disabled = !track || track.scrollLeft < 3;
      if (next) next.disabled = !track || track.scrollLeft >= maximumScroll() - 3;
    }

    function stopMotion() {
      cancelAnimationFrame(motionFrame);
      motionFrame = undefined;
      motionTimestamp = 0;
      section.dataset.videoMoving = 'false';
    }

    function canMove() {
      return automatic && !destroyed && !userPaused && !motionPreference.matches && onScreen && !document.hidden && !hovering && !focused && !touching && !editorSelected && !document.querySelector('dialog[open]') && performance.now() >= resumeAfter && maximumScroll() > 1;
    }

    function animate(timestamp) {
      motionFrame = undefined;
      if (!canMove()) { stopMotion(); return; }
      if (motionTimestamp) {
        const elapsed = Math.min(40, timestamp - motionTimestamp) / 1000;
        const maximum = maximumScroll();
        motionPosition += speed * elapsed * direction;
        if (motionPosition >= maximum) { motionPosition = maximum; direction = -1; }
        if (motionPosition <= 0) { motionPosition = 0; direction = 1; }
        track.scrollLeft = motionPosition;
      }
      motionTimestamp = timestamp;
      motionFrame = requestAnimationFrame(animate);
    }

    function refreshMotion() {
      if (!automatic || destroyed) return;
      section.dataset.videoReducedMotion = String(motionPreference.matches);
      if (motionButton) {
        motionButton.hidden = maximumScroll() <= 1;
        motionButton.disabled = motionPreference.matches;
        motionButton.setAttribute('aria-pressed', String(userPaused));
        motionButton.setAttribute('aria-label', motionPreference.matches ? t('videos.motion_reduced', {}, "Automatic scrolling is off because reduced motion is enabled") : userPaused ? t('videos.play_scroll', {}, "Play automatic scrolling") : t('videos.pause_scroll', {}, "Pause automatic scrolling"));
        const label = motionButton.querySelector('[data-video-motion-label]');
        const icon = motionButton.querySelector('[data-video-motion-icon]');
        if (label) label.textContent = motionPreference.matches ? t('videos.motion_off', {}, "Motion off") : userPaused ? t('videos.play', {}, "Play") : t('videos.pause', {}, "Pause");
        if (icon) icon.textContent = userPaused ? '▶' : 'Ⅱ';
      }
      if (canMove()) {
        if (motionFrame === undefined) {
          motionPosition = track.scrollLeft;
          motionTimestamp = 0;
          section.dataset.videoMoving = 'true';
          motionFrame = requestAnimationFrame(animate);
        }
      } else stopMotion();
    }

    function holdForManualBrowse() {
      if (!automatic) return;
      resumeAfter = performance.now() + 5000;
      clearTimeout(resumeTimer);
      stopMotion();
      resumeTimer = setTimeout(() => { resumeAfter = 0; refreshMotion(); }, 5000);
    }

    function go(directionToMove) {
      if (!track) return;
      holdForManualBrowse();
      const index = Math.min(cards.length - 1, Math.max(0, currentIndex() + directionToMove));
      track.scrollTo({ left: position(index), behavior: behavior() });
      if (status) status.textContent = t('videos.story_count', { index: index + 1, count: cards.length }, 'Video story {index} of {count}');
    }

    function releasePlayer() {
      embedRequest++;
      clearTimeout(embedTimeout);
      if (playerLoad) playerFrame?.removeEventListener('load', playerLoad);
      playerFrame = undefined;
      playerLoad = undefined;
      playerObserver?.disconnect();
      playerObserver = undefined;
      const video = content?.querySelector('video');
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        video.load();
      }
      content?.querySelectorAll('iframe').forEach(iframe => iframe.removeAttribute('src'));
      content?.replaceChildren();
    }

    on(previous, 'click', () => go(-1));
    on(next, 'click', () => go(1));
    on(track, 'scroll', () => {
      cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(updateControls);
    }, { passive: true });
    on(track, 'keydown', event => {
      if (event.target !== track) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        go(event.key === 'ArrowLeft' ? -1 : 1);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        holdForManualBrowse();
        const index = event.key === 'Home' ? 0 : cards.length - 1;
        track.scrollTo({ left: position(index), behavior: behavior() });
      }
    });

    on(section, 'click', event => {
      const button = event.target.closest('[data-video-open]');
      if (!button || !section.contains(button) || !dialog || !content) return;
      const template = button.closest('[data-video-card]')?.querySelector('[data-video-template]');
      if (!template || dialog.open) return;
      const openDialogs = [...document.querySelectorAll('dialog[open]')];
      previousOverflow = openDialogs.length ? '' : document.body.style.overflow;
      openDialogs.forEach(openDialog => openDialog.close());
      trigger = button;
      content.replaceChildren(template.content.cloneNode(true));
      const instagram = content.querySelector('[data-instagram-permalink]');
      const permalink = instagram?.dataset.instagramPermalink || '';
      const title = content.querySelector('[data-video-story-title]');
      if (title) {
        title.id = dialog.id + '-title';
        dialog.setAttribute('aria-labelledby', title.id);
      }
      dialog.showModal();
      if (instagram && /^https:\/\/www\.instagram\.com\/(?:reel|p)\/[A-Za-z0-9_-]+\/$/.test(permalink)) {
        const request = ++embedRequest;
        const notice = instagram.querySelector('[data-instagram-status]');
        let ready = false;
        const markReady = () => {
          if (request !== embedRequest || !instagram.isConnected) return;
          ready = true;
          clearTimeout(embedTimeout);
          if (notice) notice.hidden = true;
          if (playerLoad) playerFrame?.removeEventListener('load', playerLoad);
          playerLoad = undefined;
          playerObserver?.disconnect();
        };
        const fallback = () => {
          if (ready || request !== embedRequest || !instagram.isConnected) return;
          clearTimeout(embedTimeout);
          playerObserver?.disconnect();
          if (playerLoad) playerFrame?.removeEventListener('load', playerLoad);
          playerLoad = undefined;
          instagram.querySelectorAll('iframe').forEach(iframe => iframe.removeAttribute('src'));
          const link = document.createElement('a');
          link.className = 'social-instagram-fallback';
          link.href = permalink; link.target = '_blank'; link.rel = 'noopener';
          const poster = button.querySelector('img')?.cloneNode(true);
          if (poster) link.append(poster);
          const text = document.createElement('span');
          text.textContent = t('videos.watch_instagram', {}, "Watch on Instagram ↗");
          link.append(text);
          instagram.replaceChildren(link);
        };
        const quote = document.createElement('blockquote');
        quote.className = 'instagram-media';
        quote.setAttribute('data-instgrm-permalink', `${permalink}?utm_source=ig_embed&utm_campaign=loading`);
        quote.setAttribute('data-instgrm-version', '14');
        const original = document.createElement('a');
        original.href = permalink; original.target = '_blank'; original.rel = 'noopener';
        original.textContent = t('videos.view_instagram', {}, "View this post on Instagram");
        quote.append(original); instagram.append(quote);
        // Use Instagram's official renderer so it supplies the iframe URL,
        // embed parameters and resizing protocol required by its current player.
        const observePlayer = () => {
          const iframe = instagram.querySelector('iframe');
          if (!iframe || iframe === playerFrame) return;
          playerFrame = iframe;
          playerLoad = markReady;
          iframe.addEventListener('load', playerLoad, { once: true });
        };
        playerObserver = new MutationObserver(observePlayer);
        playerObserver.observe(instagram, { childList: true, subtree: true });
        embedTimeout = setTimeout(fallback, 18000);
        loadInstagramSdk().then(sdk => {
          if (request !== embedRequest || !dialog.open || !instagram.isConnected) return;
          sdk.Embeds.process();
          observePlayer();
        }).catch(fallback);
      }
      document.body.style.overflow = 'hidden';
      dialog.querySelector('[data-video-close]')?.focus();
      refreshMotion();
      content.querySelector('video')?.play().catch(() => { /* Native controls remain available when playback needs another tap. */ });
    });

    on(dialog?.querySelector('[data-video-close]'), 'click', () => dialog.close());
    on(window, 'message', event => {
      if (playerFrame?.contentWindow && event.origin === 'https://www.instagram.com' && event.source === playerFrame.contentWindow) playerLoad?.();
    });
    on(dialog, 'click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    });
    on(dialog, 'close', () => {
      // Native close events are queued: a different modal may already be open.
      if (dialog.open) return;
      releasePlayer();
      const anotherDialogOpen = document.querySelector('dialog[open]');
      document.body.style.overflow = anotherDialogOpen ? 'hidden' : previousOverflow;
      if (!anotherDialogOpen && trigger?.isConnected) trigger.focus({ preventScroll: true });
      refreshMotion();
    });

    on(section, 'shopify:block:select', event => {
      editorSelected = true;
      refreshMotion();
      const index = cards.indexOf(event.target.closest('[data-video-card]'));
      if (index >= 0 && track) track.scrollTo({ left: position(index), behavior: behavior() });
    });
    on(section, 'shopify:block:deselect', () => { editorSelected = false; refreshMotion(); });

    let intersectionObserver;
    let modalObserver;
    if (automatic) {
      on(motionButton, 'click', () => { userPaused = !userPaused; if (!userPaused && document.activeElement === motionButton) focused = false; refreshMotion(); });
      on(section, 'pointerenter', event => { if (event.pointerType === 'mouse') { hovering = true; refreshMotion(); } });
      on(section, 'pointerleave', event => { if (event.pointerType === 'mouse') { hovering = false; refreshMotion(); } });
      on(section, 'focusin', () => { focused = true; refreshMotion(); });
      on(section, 'focusout', event => { focused = !!event.relatedTarget && section.contains(event.relatedTarget); refreshMotion(); });
      on(track, 'pointerdown', () => { touching = true; holdForManualBrowse(); refreshMotion(); }, { passive: true });
      const finishTouch = () => { if (!touching) return; touching = false; holdForManualBrowse(); refreshMotion(); };
      on(document, 'pointerup', finishTouch, { passive: true });
      on(document, 'pointercancel', finishTouch, { passive: true });
      on(track, 'wheel', holdForManualBrowse, { passive: true });
      on(document, 'visibilitychange', refreshMotion);
      on(motionPreference, 'change', refreshMotion);
      if ('IntersectionObserver' in window) {
        intersectionObserver = new IntersectionObserver(entries => { onScreen = entries[0].isIntersecting; refreshMotion(); }, { threshold: 0 });
        intersectionObserver.observe(section);
      }
      modalObserver = new MutationObserver(refreshMotion);
      modalObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
    }

    const resizeObserver = track ? new ResizeObserver(() => { updateControls(); refreshMotion(); }) : null;
    if (track) resizeObserver.observe(track);
    updateControls();
    refreshMotion();
    on(section, 'nlv:video-cleanup', () => {
      destroyed = true;
      stopMotion();
      clearTimeout(resumeTimer);
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      modalObserver?.disconnect();
      cancelAnimationFrame(scrollFrame);
      releasePlayer();
      if (dialog?.open) {
        dialog.close();
        document.body.style.overflow = document.querySelector('dialog[open]') ? 'hidden' : previousOverflow;
      }
      controller.abort();
      initialized.delete(section);
    }, { once: true });
  }

  function scan(root = document) {
    if (root.matches?.('[data-video-stories]')) initialize(root);
    root.querySelectorAll('[data-video-stories]').forEach(initialize);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.('[data-video-stories]')) event.target.dispatchEvent(new Event('nlv:video-cleanup'));
    event.target.querySelectorAll('[data-video-stories]').forEach(section => section.dispatchEvent(new Event('nlv:video-cleanup')));
  });
})();
